import {useEffect, useRef, useCallback} from 'react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useTpaMarketingConsent from '~/app/hooks/use-tpa-marketing-consent'
import {trackConsentForSegment, identifyUser} from './eventHandlers'
import {consentEventCategories, sessionKeys} from './constants'
import {formatSiteName} from '../helpers'
import {isClient} from 'hec/utils/common'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

export const TpaConsentTracker = () => {
    const {data: customer} = useCurrentCustomer()
    const {canTrackMarketingEvents, isTpaUser, onConsentChange} = useTpaMarketingConsent()
    const {site} = useMultiSite()
    const lastConsentState = useRef(null)
    const hasTrackedInitial = useRef(
        typeof window !== 'undefined' &&
            sessionStorage.getItem(sessionKeys.consentTracked) === 'true'
    )

    const trackConsent = useCallback(
        async (placement) => {
            const canTrack = canTrackMarketingEvents()
            if (lastConsentState.current === canTrack || !canTrack) return
            if (!customer?.email) return

            // Set the guard before awaiting so concurrent invocations don't
            // double-fire while identify is pending.
            lastConsentState.current = canTrack

            // Await identify so the userId is set on the analytics instance
            // before the consent tracks fire — source-side consent middleware
            // drops userId-less consents. SFCC-10693.
            await identifyUser(customer.email, {email: customer.email})

            const acceptAction = 'accept'
            const formattedSiteName = formatSiteName(site.id)

            trackConsentForSegment({
                category: formattedSiteName + consentEventCategories.hecMarketingEmail,
                action: acceptAction,
                placement
            })
            trackConsentForSegment({
                category: formattedSiteName + consentEventCategories.hecSaleEmail,
                action: acceptAction,
                placement
            })
            if (site.id === 'HSASTORE') {
                trackConsentForSegment({
                    category: formattedSiteName + consentEventCategories.hecEducationEmail,
                    action: acceptAction,
                    placement
                })
            }
        },
        [canTrackMarketingEvents, site.id, customer?.email]
    )

    useEffect(() => {
        if (!isClient) return

        if (isTpaUser) {
            // Wait until the customer email has hydrated before firing — and
            // latching — the initial-load consent. For a first-time TPA SSO
            // arrival, isTpaUser is true at mount (the AFID is in the URL) but
            // customer.email from useCurrentCustomer() resolves asynchronously.
            // Latching before email is available made trackConsent early-return
            // (see the !customer?.email guard) while still marking the session
            // as tracked, permanently suppressing the consent. SFCC-11280
            // (regression from SFCC-10999). The effect re-runs once email
            // hydrates because trackConsent/canTrackMarketingEvents depend on it.
            if (!hasTrackedInitial.current && customer?.email) {
                void trackConsent('tpa-initial-load')
                hasTrackedInitial.current = true
                sessionStorage.setItem(sessionKeys.consentTracked, 'true')
            }
            const unsubscribe = onConsentChange(() => {
                void trackConsent('tpa-consent-change')
            })
            return () => {
                unsubscribe?.()
            }
        }
    }, [canTrackMarketingEvents, isTpaUser, trackConsent, onConsentChange, customer?.email])

    return null
}
